import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/** Các mức số bản ghi/trang của hệ cũ. */
export const PAGE_SIZES = [10, 20, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;

type Props = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
};

/**
 * Thanh phân trang giống hệ cũ: bên trái là ô chọn "Hiển thị N bản ghi/trang",
 * bên phải là "[ x - y / tổng ]" kèm nút về đầu / lùi / tiến / về cuối.
 *
 * Trước đây mỗi màn tự chế một khối "Trước / Trang a/b / Sau" nên người dùng không
 * đổi được số dòng và không biết tổng số bản ghi — hai thứ hệ cũ luôn hiển thị.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  className,
}: Props) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), lastPage);
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(current * pageSize, total);

  const navBtn =
    "h-7 w-7 grid place-items-center rounded-sm border border-border text-muted-foreground " +
    "hover:bg-muted disabled:opacity-40 disabled:pointer-events-none";

  return (
    <div
      className={cn("mt-3 flex flex-wrap items-center justify-between gap-3 text-sm", className)}
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <span>Hiển thị</span>
        <select
          className="h-7 rounded-sm border border-border bg-background px-1.5 text-sm"
          value={pageSize}
          onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          disabled={!onPageSizeChange}
          aria-label="Hiển thị"
        >
          {[...new Set([...PAGE_SIZES, pageSize])]
            .sort((a, b) => a - b)
            .map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
        </select>
        <span>bản ghi/trang</span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-muted-foreground tabular-nums">
          [ {from.toLocaleString("vi-VN")} - {to.toLocaleString("vi-VN")} /{" "}
          {total.toLocaleString("vi-VN")} ]
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={navBtn}
            disabled={current === 1}
            onClick={() => onPageChange(1)}
            aria-label="Trang đầu"
          >
            <ChevronFirst className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className={navBtn}
            disabled={current === 1}
            onClick={() => onPageChange(current - 1)}
            aria-label="Trang trước"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="px-2 tabular-nums">
            {current} / {lastPage}
          </span>
          <button
            type="button"
            className={navBtn}
            disabled={current >= lastPage}
            onClick={() => onPageChange(current + 1)}
            aria-label="Trang sau"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className={navBtn}
            disabled={current >= lastPage}
            onClick={() => onPageChange(lastPage)}
            aria-label="Trang cuối"
          >
            <ChevronLast className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
